import type { components } from '@/generated/internal-api/openapi-types';
import type { Color } from '@/model/Common.model';

export type TeamConfigResponse = components['schemas']['TeamConfigV1.schema'];
export type TeamConfigUpdate = components['schemas']['TeamConfigUpdateV1.schema'];
export type TeamTheme = components['schemas']['TeamThemeV1.schema'];

export interface TeamConfigFormValues {
  theme: {
    active: boolean;
    value: {
      primary: string;
      on_primary: string;
    };
  };
  studies_read_all: {
    active: boolean;
  };
}

const srgbToLinear = (c: number): number => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));

const parseHex = (hex: string): { r: number; g: number; b: number } => {
  const h = hex.replace('#', '');
  return {
    r: parseInt(h.slice(0, 2), 16) / 255,
    g: parseInt(h.slice(2, 4), 16) / 255,
    b: parseInt(h.slice(4, 6), 16) / 255,
  };
};

export const relativeLuminance = (hex: string): number => {
  const { r, g, b } = parseHex(hex);
  return 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b);
};

export const wcagContrastRatio = (hex1: string, hex2: string): number => {
  const l1 = relativeLuminance(hex1);
  const l2 = relativeLuminance(hex2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
};

export const passesWcagAA = (ratio: number): boolean => ratio >= 4.5;

export const isValidHexColor = (value: string): boolean => /^#[0-9A-Fa-f]{6}$/.test(value);

export const generateRandomTheme = (): { primary: string; onPrimary: string } => {
  const randomHex = (): string => {
    const hex = Math.floor(Math.random() * 0xffffff)
      .toString(16)
      .padStart(6, '0');
    return `#${hex}`;
  };

  const primary = randomHex();
  const primaryLuminance = relativeLuminance(primary);
  const onPrimary = primaryLuminance > 0.179 ? '#000000' : '#ffffff';

  return { primary, onPrimary };
};

export const buildPatchPayload = (
  formValues: TeamConfigFormValues,
  dirtyFields: Record<string, unknown>
): TeamConfigUpdate => {
  const payload: TeamConfigUpdate = {};

  if ('theme' in dirtyFields) {
    payload.theme = {
      active: formValues.theme.active,
      value: {
        primary: formValues.theme.value.primary as Color,
        on_primary: formValues.theme.value.on_primary as Color,
      },
    };
  }

  if ('studies_read_all' in dirtyFields) {
    payload.studies_read_all = {
      active: formValues.studies_read_all.active,
    };
  }

  return payload;
};
